import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, useEffect, useContext } from "react";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingOverlay from "react-loading-overlay";
LoadingOverlay.propTypes = undefined;
import Moment from "moment";
import Select from "react-select";

import ReactBSAlert from "react-bootstrap-sweetalert";
import { MainContext } from "context/MainContext.js";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";

function AlarmList(props) {
  const location = useLocation();
  const history = useHistory();

  const [showLoading, setShowLoading] = useState(false);
  const privileges = useContext(MainContext);

  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [alert, setAlert] = useState(null);

  const [params, setParams] = useState(location.state?.value ?? "");
  const [paramsReady, setParamsReady] = useState(false);

  const filterButtonsOptions = [
    { value: "status||active", label: "Active Alarms", key: "active" },
    { value: "status||history", label: "Events History", key: "history" },
  ];

  useEffect(() => {
    if (location.state != null && params != location.state.params) {
      setParams(location.state.params);
      setForceRefreshCount((prevValue) => prevValue + 1);
    }

    setParamsReady(true);
  });

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["alarms.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const redirectToDeviceDetails = (serialNumber) => {
    const location = {
      pathname: "/admin/devices/details",
      state: {
        value: serialNumber,
      },
    };
    history.push(location);
  };

  const openDevicePortal = (serialNumber) => {
    fetch("/api/v1/device/url", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
        serialNumber: serialNumber,
        virtuosoPort: window.location.port,
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        window.open(actualData.data.url, "_blank", "noreferrer");
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const testRadiusConnection = (details) => {
    setShowLoading(true);
    fetch("/api/v1/alarm/radius/test", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
        alarmDetails: details,
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (!actualData.data) {
          toast.error("The radius server is invalid", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        } else {
          setForceRefreshCount((prevValue) => prevValue + 1);
          toast.success("The radius server is valid", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        }
        setShowLoading(false);
      })
      .catch((err) => {
        console.log(err.message);
        setShowLoading(false);
      });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const filterButtons = useMemo(() => {
    let defaultValue = filterButtonsOptions.find((c) => c.value == params);
    if (defaultValue == undefined) {
      defaultValue = filterButtonsOptions[0];
    }

    if (params && params != "" && !params.includes("status")) {
      return "";
    } else {
      return (
        <Select
          className="react-select primary"
          classNamePrefix="react-select"
          name="filterButton"
          defaultValue={defaultValue}
          onChange={(option) => {
            setParams(option.value.value);
            setForceRefreshCount((prevValue) => prevValue + 1);
          }}
          isHidden="true"
          options={filterButtonsOptions.map((option) => ({
            value: {
              ...option,
              toString: () => `${option.value}`,
            },
            label: `${option.label}`,
          }))}
        />
      );
    }
  });

  const showRadiusReconnectButton = (condition, details) => {
    if (
      condition == "Invalid Server RADIUS" &&
      Utilities.hasAccess(privileges["alarms.radius.test"])
    ) {
      return (
        <>
          <DropdownItem onClick={() => testRadiusConnection(details)}>
            Test RADIUS Server
          </DropdownItem>
          <DropdownItem divider />
        </>
      );
    } else {
      return <></>;
    }
  };

  const showDetailsButton = (detailsValue) => {
    if (detailsValue) {
      return (
        <DropdownItem
          onClick={() =>
            setAlert(
              <ReactBSAlert
                style={{ display: "block", marginTop: "-100px" }}
                onConfirm={() => hideAlert()}
                onCancel={() => hideAlert()}
                confirmBtnBsStyle="info"
                btnSize=""
              >
                {detailsValue}
              </ReactBSAlert>,
            )
          }
        >
          Show Details
        </DropdownItem>
      );
    } else {
      return <></>;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "System Name",
        accessor: "systemName",
        sortBy: "device.name",
        Cell: ({ row: { original }, value }) => {
          const hasGamDetails = !!original.deviceSerialNumber;

          if (!hasGamDetails) {
            return <span>{value}</span>;
          }

          return (
            <a
              onClick={() =>
                redirectToDeviceDetails(original.deviceSerialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {value}
            </a>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Serial Number",
        accessor: "deviceSerialNumber",
        sortBy: "device.serialNumber",
        flex: "1 1 0",
        show: false,
        filterable: false,
        disableExport: true,
      },
      {
        Header: "Product Class",
        accessor: "productClass",
        sortBy: "device.productClass",
        flex: "1 1 0",
      },
      {
        Header: "Severity",
        accessor: "severity",
        sortBy: "severity",
        Cell: (props) => {
          let value = props.value.toUpperCase();

          // Set the right background color base on the delay
          var color = "";
          if (value == "CR") {
            color = "rgba(232, 80, 77, 1)"; // Muted Red
          } else if (value == "MJ") {
            color = "rgba(237, 145, 49, 1)"; // Soft Orange
          } else if (value == "MN") {
            color = "rgba(240, 205, 102, 1)"; // Soft Yellow
          } else if (value == "NA") {
            color = "rgba(149, 179, 215, 1)"; // Light Steel Blue
          }
          return (
            <div style={{ backgroundColor: color, width: "100%" }}>{value}</div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Condition",
        accessor: "condition",
        sortBy: "condType",
        flex: "1 1 0",
      },
      {
        Header: "Service Affecting",
        accessor: "serviceAffecting",
        sortBy: "servAff",
        Cell: (props) => {
          return <span> {props.value}</span>;
        },
        flex: "1 1 0",
      },
      {
        Header: "Description",
        accessor: "description",
        sortBy: "ifDescr",
        flex: "1 1 0",
      },
      {
        Header: "Occured",
        accessor: "occured",
        sortBy: "occurTime",
        Cell: (props) => {
          return (
            <div>
              {props.settings.general_convert_date_with_user_timezone === "true"
                ? Moment(props.value)
                    .tz(props.settings.user_timezone)
                    .format("yy-MM-DD hh:mm a")
                : Moment.utc(props.value).format("yy-MM-DD hh:mm a")}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Received",
        accessor: "received",
        sortBy: "creationDate",
        Cell: (props) => {
          return (
            <div>
              {props.settings.general_convert_date_with_user_timezone === "true"
                ? Moment(props.value)
                    .tz(props.settings.user_timezone)
                    .format("yy-MM-DD hh:mm a")
                : Moment.utc(props.value).format("yy-MM-DD hh:mm a")}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Cleared",
        accessor: "cleared",
        sortBy: "closingDate",
        Cell: (props) => {
          return (
            <div>
              {props.value
                ? props.settings.general_convert_date_with_user_timezone ===
                  "true"
                  ? Moment(props.value)
                      .tz(props.settings.user_timezone)
                      .format("yy-MM-DD hh:mm:ss a")
                  : Moment.utc(props.value).format("yy-MM-DD hh:mm a")
                : ""}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "details",
        Cell: ({ row: { original }, value }) => {
          if (original.condition === "Virtuoso License Expiring") return null;

          const hasRadiusReconnect = showRadiusReconnectButton(
            original.condition,
            value,
          );
          const hasDetails = showDetailsButton(value);
          const hasGamDetails = !!original.deviceSerialNumber;
          const hasGamPortal =
            sessionStorage
              .getItem("permissions")
              ?.includes("GAM_PORTAL_ACCESS") && original.deviceSerialNumber;

          if (
            !hasRadiusReconnect &&
            !hasDetails &&
            !hasGamDetails &&
            !hasGamPortal
          )
            return null;

          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{ padding: "6px 14px", margin: 0 }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container="body">
                {hasRadiusReconnect &&
                  showRadiusReconnectButton(original.condition, value)}
                {hasDetails && showDetailsButton(value)}
                {hasGamDetails && (
                  <DropdownItem
                    onClick={() =>
                      redirectToDeviceDetails(original.deviceSerialNumber)
                    }
                  >
                    View GAM Details
                  </DropdownItem>
                )}
                {hasGamPortal && (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() =>
                        openDevicePortal(original.deviceSerialNumber)
                      }
                    >
                      Open GAM Portal
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  const getBreadcrumbInfo = () => {
    var info = "";
    if (params && params.includes("severity")) {
      let paramsArr = params.split("||");
      let paramsValue = paramsArr[1];
      if (paramsValue == "cr") {
        info = "Critical";
      } else if (paramsValue == "mj") {
        info = "Major";
      } else if (paramsValue == "mn") {
        info = "Minor";
      } else if (paramsValue == "na") {
        info = "Non-Alarm";
      }
    } else if (params == "History24hCritical") {
      info = "Recent Critical Alarms";
    }

    return info;
  };
  return (
    <>
      <ToastContainer />

      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Testing RADIUS Connection"
          className={showLoading ? "loading" : ""}
        />
        {alert}
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Monitoring</BreadcrumbItem>
                  <BreadcrumbItem>
                    Alarms{" "}
                    {getBreadcrumbInfo()
                      ? " (" + getBreadcrumbInfo() + ")"
                      : ""}
                  </BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                {paramsReady && (
                  <ReactTable
                    query="/api/v1/alarm/list"
                    columns={columns}
                    title="Alarms"
                    param={params}
                    filterButtons={filterButtons}
                    forceRefresh={forceRefreshCount}
                    className="-striped -highlight primary-pagination"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default AlarmList;
